import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  ShieldCheck, Package, Warehouse, CheckCircle2, Clock,
  Trash2, Wrench, Search, RefreshCw, AlertTriangle, X
} from "lucide-react";
import { toast } from "sonner";

const STAGE_META = {
  submitted:            { label: "Submitted",            color: "bg-slate-100 text-slate-700",   icon: Clock },
  dirty_received:       { label: "Decontamination — Received", color: "bg-amber-100 text-amber-800",   icon: ShieldCheck },
  packaging_received:   { label: "Packaging — Received",       color: "bg-blue-100 text-blue-800",     icon: Package },
  storage_received:     { label: "Sterile Area — Ready",       color: "bg-purple-100 text-purple-800", icon: Warehouse },
  completed:            { label: "Sterile Area — Completed", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
};

// per-instrument stage keys (must match backend)
const INST_SUBMITTED = "submitted";
const INST_DIRTY = "dirty_received";
const INST_PACKAGING = "packaging_received";
const INST_STORAGE = "storage_received";
const INST_COMPLETED = "completed";
const INST_MISSING = "missing";

const ROLE_STAGE = {
  sterilization_dirty:      { inputInstStage: INST_SUBMITTED,  endpoint: "receive-dirty",     mode: "dirty",   actionLabel: "Receive (Decontamination)" },
  sterilization_packaging:  { inputInstStage: INST_DIRTY,      endpoint: "receive-packaging", mode: "partial", actionLabel: "Receive (Packaging)" },
  sterilization_storage:    { inputInstStage: INST_PACKAGING,  endpoint: "receive-storage",   mode: "partial", actionLabel: "Receive (Sterile Area)" },
};

const Sterilization = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.role === "admin";
  const roleCfg = ROLE_STAGE[user?.role];
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const defaultTab = isAdmin ? "monitor" : "queue";
  const [tab, setTab] = useState(defaultTab);

  // Data
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Dialogs
  const [detail, setDetail] = useState(null);
  const [actionDialog, setActionDialog] = useState(null); // { request }
  const [actionComment, setActionComment] = useState("");
  const [instrumentSelection, setInstrumentSelection] = useState({}); // for partial: {id: true}; for dirty: {id: 'received'|'missing'}
  const [zoomedImage, setZoomedImage] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [reqRes, statsRes] = await Promise.all([
        axios.get(`${API}/sterilization/requests`, { headers: authHeaders }),
        axios.get(`${API}/sterilization/stats`, { headers: authHeaders }),
      ]);
      setRequests(reqRes.data || []);
      setStats(statsRes.data || {});
      if (isAdmin) {
      }
    } catch (e) {
      toast.error("Failed to load sterilization data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const deleteRequest = async (r) => {
    if (!window.confirm(`Delete request ${r.tracking_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/sterilization/requests/${r.id}`, { headers: authHeaders });
      toast.success(`Request ${r.tracking_number} deleted`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // ===== Sound alert on new incoming items for this stage =====
  const prevPendingRef = useRef(null);

  // Distinct chime per department (Decontamination / Packaging / Sterile)
  const CHIME_PATTERNS = {
    sterilization_dirty:     { wave: "triangle", notes: [[740, 0, 0.14], [740, 0.18, 0.14], [988, 0.36, 0.3]] },   // triple alert
    sterilization_packaging: { wave: "sine",     notes: [[880, 0, 0.18], [1174.66, 0.18, 0.28]] },                 // rising chime
    sterilization_storage:   { wave: "sine",     notes: [[1318.5, 0, 0.16], [987.77, 0.16, 0.16], [783.99, 0.32, 0.3]] }, // descending bell
  };

  const playChime = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const pattern = CHIME_PATTERNS[user?.role] || CHIME_PATTERNS.sterilization_packaging;
      const note = (freq, start, dur) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = pattern.wave;
        o.frequency.value = freq;
        o.connect(g);
        g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
        o.start(ctx.currentTime + start);
        o.stop(ctx.currentTime + start + dur + 0.05);
      };
      pattern.notes.forEach(([f, s, d]) => note(f, s, d));
      setTimeout(() => ctx.close(), 1500);
    } catch (e) { /* audio not allowed yet */ }
  };

  useEffect(() => {
    if (!roleCfg) return;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/sterilization/requests`, { headers: authHeaders });
        const data = res.data || [];
        const pendingIds = data.filter(r => (r.instruments || []).some(i => i.stage === roleCfg.inputInstStage)).map(r => r.id);
        if (prevPendingRef.current !== null) {
          const hasNew = pendingIds.some(id => !prevPendingRef.current.includes(id));
          if (hasNew) {
            playChime();
            toast.info("New request arrived in your queue", { duration: 6000 });
          }
        }
        prevPendingRef.current = pendingIds;
        setRequests(data);
      } catch { /* silent */ }
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleCfg?.inputInstStage, token]);

  // ===== Instant push notification via WebSocket (fallback: polling above) =====
  useEffect(() => {
    if (!roleCfg) return;
    let ws = null;
    let alive = true;
    let retryTimer = null;

    const silentRefreshQueue = () => {
      axios.get(`${API}/sterilization/requests`, { headers: authHeaders }).then(r => {
        const data = r.data || [];
        prevPendingRef.current = data
          .filter(x => (x.instruments || []).some(i => i.stage === roleCfg.inputInstStage))
          .map(x => x.id);
        setRequests(data);
      }).catch(() => {});
    };

    const connect = () => {
      try {
        const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/^http/, "ws");
        ws = new WebSocket(`${base}/api/ws/sterilization`);
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === "steri_event" && msg.stage === roleCfg.inputInstStage) {
              playChime();
              toast.info("New request arrived in your queue", { duration: 6000 });
              silentRefreshQueue();
            }
          } catch { /* ignore */ }
        };
        ws.onclose = () => { if (alive) retryTimer = setTimeout(connect, 5000); };
        ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
      } catch { /* ws unsupported */ }
    };
    connect();
    return () => { alive = false; clearTimeout(retryTimer); try { ws && ws.close(); } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleCfg?.inputInstStage, token]);

  const openDetail = async (req) => {
    try {
      const r = await axios.get(`${API}/sterilization/requests/${req.id}`, { headers: authHeaders });
      setDetail(r.data);
    } catch {
      toast.error("Failed to load request details");
    }
  };

  const openActionDialog = (request) => {
    setActionDialog({ request });
    setActionComment("");
    // No pre-selection: every item must be explicitly classified (dirty) or ticked (partial).
    setInstrumentSelection({});
  };

  const submitStageAction = async () => {
    if (!actionDialog || !roleCfg) return;
    const req = actionDialog.request;
    try {
      let payload;
      if (roleCfg.mode === "dirty") {
        const confirmed_ids = [];
        const missing_ids = [];
        for (const inst of (req.instruments || [])) {
          if (inst.stage !== roleCfg.inputInstStage) continue;
          const decision = instrumentSelection[inst.instrument_id];
          if (decision === "received") confirmed_ids.push(inst.instrument_id);
          else if (decision === "missing") missing_ids.push(inst.instrument_id);
          else {
            toast.error(`Please classify "${inst.name_en}" as Received or Missing`);
            return;
          }
        }
        if (confirmed_ids.length === 0) {
          toast.error("At least one instrument must be marked as Received");
          return;
        }
        payload = { confirmed_ids, missing_ids, comment: actionComment };
      } else {
        const instrument_ids = Object.keys(instrumentSelection).filter(id => instrumentSelection[id]);
        if (instrument_ids.length === 0) {
          toast.error("Select at least one instrument to receive");
          return;
        }
        payload = { instrument_ids, comment: actionComment };
      }
      await axios.post(
        `${API}/sterilization/requests/${req.id}/${roleCfg.endpoint}`,
        payload,
        { headers: authHeaders }
      );
      toast.success("Handoff recorded successfully");
      setActionDialog(null);
      setActionComment("");
      setInstrumentSelection({});
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const filteredRequests = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return requests;
    return requests.filter(r =>
      (r.tracking_number || "").toLowerCase().includes(s) ||
      (r.clinic_name || "").toLowerCase().includes(s) ||
      (`clinic ${r.clinic_number || ""}`).toLowerCase().includes(s) ||
      (r.reporter_name || "").toLowerCase().includes(s)
    );
  }, [requests, search]);

  const myQueue = useMemo(() => {
    if (!roleCfg) return [];
    return requests.filter(r =>
      (r.instruments || []).some(i => i.stage === roleCfg.inputInstStage)
    );
  }, [requests, roleCfg]);

  const pendingCount = (r) =>
    (r.instruments || []).filter(i => i.stage === roleCfg?.inputInstStage).length;

  const stageBadge = (stage) => {
    const meta = STAGE_META[stage] || STAGE_META.submitted;
    const Icon = meta.icon;
    return (
      <Badge className={`${meta.color} border-0 gap-1`} data-testid={`stage-badge-${stage}`}>
        <Icon className="w-3 h-3" />
        {meta.label}
      </Badge>
    );
  };

  const renderStatsCards = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 lg:gap-3 mb-6" dir="ltr">
      {Object.entries(STAGE_META).filter(([key]) => key !== "storage_received").map(([key, meta]) => {
        const val = stats[key.replace("_received", "")] ?? stats[key] ?? 0;
        const Icon = meta.icon;
        return (
          <Card key={key} className="border-0 shadow-sm h-full">
            <CardContent className="p-3 lg:p-4 h-full flex flex-col gap-2">
              <p className="text-[11px] lg:text-xs font-semibold text-slate-500 leading-snug w-full">{meta.label}</p>
              <div className="flex items-center justify-between mt-auto">
                <p className="text-xl lg:text-3xl font-bold text-[#4a3fa8]">{val}</p>
                <div className={`w-8 h-8 lg:w-10 lg:h-10 shrink-0 rounded-lg flex items-center justify-center ${meta.color}`}>
                  <Icon className="w-4 h-4 lg:w-5 lg:h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const clinicLabel = (r) => (r.clinic_number ? `Clinic ${r.clinic_number}` : r.clinic_name);

  const formatRiyadh = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-GB", {
        timeZone: "Asia/Riyadh",
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        hour12: false,
      });
    } catch { return iso; }
  };

  const renderRequestsTable = (rows, showAction) => (
    <div className="border rounded-lg overflow-x-auto bg-white" dir="ltr">
      <Table>
        <TableHeader>
          <TableRow className="text-xs lg:text-sm">
            <TableHead className="whitespace-nowrap">Tracking No</TableHead>
            <TableHead className="whitespace-nowrap">Clinic</TableHead>
            <TableHead className="whitespace-nowrap">Requester</TableHead>
            <TableHead className="whitespace-nowrap">Items</TableHead>
            <TableHead className="whitespace-nowrap">Stage</TableHead>
            <TableHead className="whitespace-nowrap">Created</TableHead>
            <TableHead className="text-right whitespace-nowrap">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No requests</TableCell></TableRow>
          )}
          {rows.map((r) => {
            const missingCount = (r.instruments || []).filter(i => i.stage === INST_MISSING).length;
            return (
            <TableRow key={r.id} data-testid={`row-${r.tracking_number}`} className="text-xs lg:text-sm">
              <TableCell className="font-mono font-semibold text-[#4a3fa8] whitespace-nowrap py-2">
                <div className="flex items-center gap-2">
                  <span>{r.tracking_number}</span>
                  {missingCount > 0 && (
                    <Badge
                      className="bg-red-100 text-red-800 border-0 gap-1 font-sans text-[10px] px-1.5 whitespace-nowrap"
                      title={`${missingCount} instrument(s) reported missing`}
                      data-testid={`missing-badge-${r.tracking_number}`}
                    >
                      <AlertTriangle className="w-3 h-3" />
                      {missingCount} missing
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap py-2">{clinicLabel(r)}</TableCell>
              <TableCell className="whitespace-nowrap py-2">{r.reporter_name}</TableCell>
              <TableCell className="py-2">{r.instruments?.length || 0}</TableCell>
              <TableCell className="whitespace-nowrap py-2">{stageBadge(r.current_stage)}</TableCell>
              <TableCell className="text-[11px] lg:text-xs text-slate-500 whitespace-nowrap py-2">{formatRiyadh(r.created_at)}</TableCell>
              <TableCell className="text-right space-x-2 whitespace-nowrap py-2">
                <Button size="sm" variant="ghost" onClick={() => openDetail(r)} data-testid={`view-${r.tracking_number}`}>View</Button>
                {showAction && roleCfg && (r.instruments || []).some(i => i.stage === roleCfg.inputInstStage) && (
                  <Button
                    size="sm"
                    className="bg-[#655dc6] hover:bg-[#5449a8] text-white"
                    onClick={() => openActionDialog(r)}
                    data-testid={`action-${r.tracking_number}`}
                  >
                    {roleCfg.actionLabel} ({pendingCount(r)})
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-500 hover:bg-red-50 hover:text-red-700"
                    onClick={() => deleteRequest(r)}
                    title="Delete request (admin only)"
                    data-testid={`delete-request-${r.tracking_number}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div dir="ltr" className="space-y-4" data-testid="sterilization-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#655dc6] text-white flex items-center justify-center">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#4a3fa8]">Sterilization Tracking</h2>
            <p className="text-sm text-slate-500">Full-cycle tracking for clinical instruments</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} className="gap-1.5">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {renderStatsCards()}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4">
          {roleCfg && (
            <TabsTrigger value="queue" data-testid="tab-queue">
              My Queue <Badge className="ml-2 bg-red-500 text-white">{myQueue.length}</Badge>
            </TabsTrigger>
          )}
          <TabsTrigger value="monitor" data-testid="tab-monitor">Full Monitor</TabsTrigger>
        </TabsList>

        {roleCfg && (
          <TabsContent value="queue" className="space-y-3">
            <p className="text-sm text-slate-600">Requests waiting for your action ({roleCfg.actionLabel}).</p>
            {renderRequestsTable(myQueue, true)}
          </TabsContent>
        )}

        <TabsContent value="monitor" className="space-y-3">
          <div className="flex items-center gap-2 max-w-md">
            <Search className="w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by tracking, clinic or requester..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
            />
          </div>
          {renderRequestsTable(filteredRequests, !!roleCfg)}
        </TabsContent>

      </Tabs>

      {/* Request Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" dir="ltr">
          <DialogHeader>
            <DialogTitle className="text-[#4a3fa8]">Sterilization Request Detail</DialogTitle>
            <DialogDescription>{detail?.tracking_number}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Clinic:</span> <span className="font-medium">{clinicLabel(detail)}</span></div>
                <div><span className="text-slate-500">Requester:</span> <span className="font-medium">{detail.reporter_name}</span></div>
                {isAdmin && (
                  <div><span className="text-slate-500">WhatsApp:</span> <span className="font-mono">{detail.reporter_whatsapp}</span></div>
                )}
                <div><span className="text-slate-500">Stage:</span> {stageBadge(detail.current_stage)}</div>
              </div>

              <div>
                <p className="text-xs uppercase text-slate-500 mb-2">Instruments ({detail.instruments?.length})</p>
                <ul className="text-sm space-y-1.5">
                  {detail.instruments?.map((i, idx) => {
                    const s = i.stage || INST_SUBMITTED;
                    const meta = STAGE_META[s] || STAGE_META.submitted;
                    const isMissing = s === INST_MISSING;
                    return (
                      <li key={idx} className={`flex items-center justify-between gap-3 border rounded-md p-2 ${isMissing ? "border-red-200 bg-red-50" : "bg-white"}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{i.name_en}</p>
                          {i.name_ar && <p className="text-xs text-slate-500 truncate" dir="rtl">{i.name_ar}</p>}
                        </div>
                        {isMissing ? (
                          <Badge className="bg-red-100 text-red-800 border-0">Missing</Badge>
                        ) : (
                          <Badge className={`${meta.color} border-0`}>{meta.label}</Badge>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {detail.box_photo && (
                <div>
                  <p className="text-xs uppercase text-slate-500 mb-2">Box Photo</p>
                  <img
                    src={detail.box_photo}
                    alt="Box"
                    className="w-full max-h-64 object-contain rounded border cursor-zoom-in hover:opacity-90 transition"
                    onClick={() => setZoomedImage(detail.box_photo)}
                    data-testid="box-photo-thumb"
                  />
                </div>
              )}

              {/* Lightbox — rendered inside DialogContent so Radix keeps pointer events enabled */}
              {zoomedImage && (
                <div
                  onClick={(e) => { e.stopPropagation(); setZoomedImage(null); }}
                  className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
                  style={{ pointerEvents: 'auto' }}
                  data-testid="image-lightbox"
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setZoomedImage(null); }}
                    className="absolute top-4 right-4 text-white bg-white/15 hover:bg-white/30 rounded-full p-2 transition z-10"
                    aria-label="Close"
                    data-testid="lightbox-close-btn"
                  >
                    <X className="w-6 h-6" />
                  </button>
                  <img
                    src={zoomedImage}
                    alt="Enlarged"
                    onClick={(e) => e.stopPropagation()}
                    className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg shadow-2xl cursor-default"
                  />
                </div>
              )}

              <div>
                <p className="text-xs uppercase text-slate-500 mb-2">Timeline</p>
                <div className="space-y-2">
                  {detail.timeline?.map((t, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm border-l-2 border-[#655dc6] pl-3 py-1">
                      <div>
                        <p className="font-medium">{t.label}</p>
                        <p className="text-xs text-slate-500">by {t.user} • {formatRiyadh(t.timestamp)} (Riyadh)</p>
                        {t.details && <p className="text-xs text-slate-600 italic">{t.details}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stage Action Dialog — per-instrument checklist */}
      <Dialog open={!!actionDialog} onOpenChange={(o) => { if (!o) { setActionDialog(null); setInstrumentSelection({}); setActionComment(""); } }}>
        <DialogContent dir="ltr" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{roleCfg?.actionLabel}</DialogTitle>
            <DialogDescription>
              {roleCfg?.mode === "dirty" ? (
                <>Confirm receipt of <span className="font-mono font-semibold">{actionDialog?.request?.tracking_number}</span> from the clinic. Every item must be marked as Received or Missing.</>
              ) : (
                <>Partial handoff for <span className="font-mono font-semibold">{actionDialog?.request?.tracking_number}</span>. Tick only the instruments you are receiving right now.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[50vh] overflow-y-auto border rounded-md p-3 bg-slate-50 space-y-2">
            {(actionDialog?.request?.instruments || [])
              .filter(i => i.stage === roleCfg?.inputInstStage)
              .map(inst => {
                if (roleCfg?.mode === "dirty") {
                  const val = instrumentSelection[inst.instrument_id];
                  return (
                    <div key={inst.instrument_id} className="flex items-center justify-between gap-3 bg-white border rounded-md p-2" data-testid={`row-inst-${inst.instrument_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{inst.name_en}</p>
                        {inst.name_ar && <p className="text-xs text-slate-500 truncate" dir="rtl">{inst.name_ar}</p>}
                      </div>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => setInstrumentSelection(s => ({ ...s, [inst.instrument_id]: "received" }))}
                          className={`px-3 py-1 text-xs font-medium rounded-md border transition ${val === "received" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:border-emerald-400"}`}
                          data-testid={`recv-${inst.instrument_id}`}
                        >
                          ✓ Received
                        </button>
                        <button
                          type="button"
                          onClick={() => setInstrumentSelection(s => ({ ...s, [inst.instrument_id]: "missing" }))}
                          className={`px-3 py-1 text-xs font-medium rounded-md border transition ${val === "missing" ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-600 border-slate-300 hover:border-red-400"}`}
                          data-testid={`miss-${inst.instrument_id}`}
                        >
                          ✗ Missing
                        </button>
                      </div>
                    </div>
                  );
                }
                // partial mode — checkboxes
                const checked = !!instrumentSelection[inst.instrument_id];
                return (
                  <label
                    key={inst.instrument_id}
                    className={`flex items-center gap-3 bg-white border rounded-md p-2 cursor-pointer transition ${checked ? "border-[#655dc6] bg-[#F5F3F9]" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`pick-${inst.instrument_id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setInstrumentSelection(s => ({ ...s, [inst.instrument_id]: !s[inst.instrument_id] }))}
                      className="w-4 h-4 accent-[#655dc6]"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{inst.name_en}</p>
                      {inst.name_ar && <p className="text-xs text-slate-500 truncate" dir="rtl">{inst.name_ar}</p>}
                    </div>
                  </label>
                );
              })}
            {(actionDialog?.request?.instruments || []).filter(i => i.stage === roleCfg?.inputInstStage).length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">No instruments awaiting your stage.</p>
            )}
          </div>

          {roleCfg?.mode === "dirty" && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span data-testid="dirty-classified-counter">
                {Object.values(instrumentSelection).filter(Boolean).length} / {(actionDialog?.request?.instruments || []).filter(i => i.stage === roleCfg?.inputInstStage).length} classified
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-emerald-600 hover:underline"
                  onClick={() => {
                    const all = {};
                    (actionDialog?.request?.instruments || []).filter(i => i.stage === roleCfg.inputInstStage).forEach(i => { all[i.instrument_id] = "received"; });
                    setInstrumentSelection(all);
                  }}
                  data-testid="mark-all-received-btn"
                >Mark all Received</button>
                <button
                  type="button"
                  className="text-slate-500 hover:underline"
                  onClick={() => setInstrumentSelection({})}
                  data-testid="dirty-clear-btn"
                >Clear</button>
              </div>
            </div>
          )}

          {roleCfg?.mode === "partial" && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>
                {Object.values(instrumentSelection).filter(Boolean).length} selected
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-[#655dc6] hover:underline"
                  onClick={() => {
                    const all = {};
                    (actionDialog?.request?.instruments || []).filter(i => i.stage === roleCfg.inputInstStage).forEach(i => { all[i.instrument_id] = true; });
                    setInstrumentSelection(all);
                  }}
                  data-testid="select-all-btn"
                >Select all</button>
                <button
                  type="button"
                  className="text-slate-500 hover:underline"
                  onClick={() => setInstrumentSelection({})}
                  data-testid="clear-selection-btn"
                >Clear</button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Comment (optional)</Label>
            <Textarea
              value={actionComment}
              onChange={(e) => setActionComment(e.target.value)}
              placeholder={roleCfg?.mode === "dirty" ? "e.g., reason for missing items..." : "e.g., first batch, remaining will follow..."}
              data-testid="action-comment"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionDialog(null); setInstrumentSelection({}); }}>Cancel</Button>
            <Button onClick={submitStageAction} className="bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="confirm-action-btn">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Sterilization;
